class CollectionSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.search__input');
    this.noResults = this.querySelector('.collection-search__no-results');
    this.spinner = this.querySelector('.collection-search__spinner');
    this.container = document.getElementById('ProductGridContainer');

    if (!this.input || !this.container) return;

    // Full-collection index (every product, not just what's currently loaded
    // in the DOM), built lazily on first use and cached until the filter/sort
    // state changes underneath us.
    this.index = null;
    this.indexing = null;
    this.originalContainerHTML = null;
    this.requestToken = 0;

    this.debouncedFilter = this.debounce(this.filter.bind(this), 200);
    this.input.addEventListener('input', this.debouncedFilter);

    // Facets, sorting, and infinite scroll all mutate #ProductGridContainer
    // from outside this component. Any of them invalidate our cached index
    // (it was built for the previous filter/sort state) unless the mutation
    // is one we made ourselves while applying search results.
    this.observer = new MutationObserver(() => {
      this.index = null;
      this.indexing = null;
      this.originalContainerHTML = null;
      if (this.input.value.trim() !== '') this.filter();
    });
    this.startObserving();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  startObserving() {
    this.observer.observe(this.container, { childList: true, subtree: true });
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  setLoading(isLoading) {
    this.classList.toggle('collection-search--loading', isLoading);
    if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
  }

  async fetchPage(page) {
    const grid = document.getElementById('product-grid');
    const sectionId = grid ? grid.dataset.id : null;
    if (!sectionId) return [];

    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('page', page);
    params.set('section_id', sectionId);

    const response = await fetch(`${window.location.pathname}?${params.toString()}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const pageGrid = doc.getElementById('product-grid');

    if (!pageGrid) return [];

    return Array.from(pageGrid.querySelectorAll(':scope > li')).map((li) => ({
      title: (li.querySelector('.card__heading')?.textContent || '').trim().toLowerCase(),
      html: li.outerHTML,
    }));
  }

  async buildIndex() {
    if (this.index) return this.index;
    if (this.indexing) return this.indexing;

    this.indexing = (async () => {
      const MAX_PAGES = 40;
      const BATCH_SIZE = 6;

      const items = await this.fetchPage(1);
      let page = 2;

      while (page <= MAX_PAGES) {
        const batchPages = [];
        for (let i = 0; i < BATCH_SIZE && page <= MAX_PAGES; i += 1, page += 1) {
          batchPages.push(page);
        }

        const batchResults = await Promise.all(batchPages.map((p) => this.fetchPage(p)));
        let hitEmptyPage = false;
        batchResults.forEach((pageItems) => {
          if (pageItems.length === 0) hitEmptyPage = true;
          items.push(...pageItems);
        });
        if (hitEmptyPage) break;
      }

      this.index = items;
      return items;
    })();

    return this.indexing;
  }

  renderMatches(matches) {
    this.observer.disconnect();

    const grid = document.getElementById('product-grid');
    if (grid) grid.innerHTML = matches.map((item) => item.html).join('');

    const pager = this.container.querySelector('infinite-scroll-pagination');
    if (pager) pager.remove();

    this.startObserving();
  }

  restoreOriginal() {
    if (!this.originalContainerHTML) return;

    this.observer.disconnect();
    this.container.innerHTML = this.originalContainerHTML;
    this.originalContainerHTML = null;
    this.startObserving();
  }

  async filter() {
    const query = this.input.value.trim().toLowerCase();
    const token = (this.requestToken += 1);

    if (query === '') {
      this.setLoading(false);
      if (this.noResults) this.noResults.classList.add('hidden');
      this.restoreOriginal();
      return;
    }

    if (!this.originalContainerHTML) {
      this.originalContainerHTML = this.container.innerHTML;
    }

    this.setLoading(true);
    if (this.noResults) this.noResults.classList.add('hidden');

    const index = await this.buildIndex();

    // A newer keystroke (or a facet/sort change) superseded this request.
    if (token !== this.requestToken) return;

    const matches = index.filter((item) => item.title.includes(query));
    this.renderMatches(matches);
    this.setLoading(false);

    if (this.noResults) {
      this.noResults.classList.toggle('hidden', matches.length !== 0);
    }
  }
}

customElements.define('collection-search', CollectionSearch);
